import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

/**
 * NavigationGuardContext — lets the currently mounted page declare that it
 * has unsaved work ("dirty" state). The shared sidebar / layout uses this
 * to block route changes via NavLink clicks until the user explicitly
 * resolves the dirty state (clear cart, hold, cancel order, …).
 *
 * Usage:
 *   const { setDirty } = useNavGuard();
 *   useEffect(() => {
 *     setDirty(cart.length > 0, "Άδειασε το ταμείο ή βάλε σε αναμονή");
 *     return () => setDirty(false);
 *   }, [cart.length, setDirty]);
 */
const NavGuardContext = createContext({
  dirty: false,
  message: "",
  setDirty: () => {},
});

export function NavGuardProvider({ children }) {
  const [dirty, setDirtyState] = useState(false);
  const [message, setMessage] = useState("");
  const dirtyRef = useRef(false);
  const messageRef = useRef("");

  const setDirty = useCallback((next, msg = "") => {
    dirtyRef.current = !!next;
    messageRef.current = msg;
    setDirtyState(!!next);
    setMessage(msg);
  }, []);

  // Browser-tab close / refresh — show the native confirm dialog if dirty.
  useEffect(() => {
    const handler = (e) => {
      if (!dirtyRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  return (
    <NavGuardContext.Provider value={{ dirty, message, setDirty }}>
      {children}
    </NavGuardContext.Provider>
  );
}

export function useNavGuard() {
  return useContext(NavGuardContext);
}
